/**
 * Theme registry — the single place a theme is declared.
 * =============================================================================
 * To add a theme:
 *   1. create `src/styles/themes/<id>.css` with a `[data-theme="<id>"]` block
 *      overriding TIER 1 primitives (see src/styles/primitives.css),
 *   2. `@import` it from `src/app/globals.css` AFTER semantic.css,
 *   3. add the id to THEMES below.
 *
 *
 * That is the whole job — no component changes, no rebuild to switch.
 *
 * Selection order at request time (see src/app/layout.tsx):
 *   `am_theme` cookie  ->  AM_THEME env  ->  DEFAULT_THEME
 *
 * The cookie exists so QA and preview URLs can flip themes without a deploy.
 * The env var is how an environment pins one.
 */

export const THEMES = {
	/** Default brand: neon accent. Uses the :root values, so it has no CSS file. */
	automint: "AutoMint (default)",
	/** iPhone 18 campaign: rose gradient. */
	launch: "iPhone 18 launch",
	/** Example seasonal campaign. */
	diwali: "Diwali",
	/**
	 * Verification only — deliberately garish. Overrides just the primitives,
	 * so anything still rendering brand colours under it is un-tokenized.
	 * Never ship this as a default.
	 */
	"qa-garish": "QA — garish (theme-coverage test)",
} as const;

export type ThemeId = keyof typeof THEMES;

/**
 * The campaign is currently live, so it is the default. Switching the whole app
 * back to the neon brand is a one-word change here (or the AM_THEME env var)
 * — this replaces the old compile-time LAUNCH_THEME boolean.
 */
export const DEFAULT_THEME: ThemeId = "automint";

export const THEME_COOKIE = "am_theme";

export const isThemeId = (v: unknown): v is ThemeId =>
	typeof v === "string" && Object.prototype.hasOwnProperty.call(THEMES, v);

/**
 * Resolve the active theme for a request. Accepts the cookie value directly so
 * this stays free of next/headers and is usable from anywhere.
 */
export const resolveTheme = (cookieValue?: string | null): ThemeId => {
	if (isThemeId(cookieValue)) return cookieValue;
	// Server-only on purpose. NEXT_PUBLIC_* is inlined at build time, so it
	// could not be changed without a rebuild; a plain env var is read from the
	// process at request time, so an environment can be re-pointed by setting
	// AM_THEME and restarting. resolveTheme is only ever called from
	// src/app/layout.tsx (a Server Component) — the client receives the
	// resolved value as a prop via ThemeProvider.
	const fromEnv = process.env.AM_THEME;
	if (isThemeId(fromEnv)) return fromEnv;
	return DEFAULT_THEME;
};
